import React, { useState } from "react";
import { useOnboarding } from "@/context/OnboardingContext";
import { getRelationshipScreenConfig, RelationshipType, RelationshipOption } from "./types/relationship";
import { cn } from "@/lib/utils";
import StickyOnboardingFooter from "./StickyOnboardingFooter";

const RelationshipTypeStep = () => {
  const { data, updateData, nextStep, saveStepData } = useOnboarding();
  const selectedType = data.relationshipType as RelationshipType | undefined;
  const [showError, setShowError] = useState(false);

  // Get dynamic screen config based on user profile
  const screenConfig = getRelationshipScreenConfig(
    data.role as string | undefined,
    data.gender as string | undefined,
    data.orientation as string | undefined
  );

  const handleSelectType = (type: RelationshipType) => {
    setShowError(false);
    updateData({ relationshipType: type });
  };

  const handleNext = async () => {
    if (!selectedType) {
      setShowError(true);
      return;
    }
    await saveStepData(5, data);
    nextStep();
  };

  const getAccentClasses = (accentColor: 'rose' | 'pink' | 'amber', isSelected: boolean) => {
    const baseClasses = {
      rose: {
        border: isSelected ? 'border-2 border-rose-500' : 'border-2 border-transparent hover:border-rose-500/50',
        bg: isSelected ? 'bg-rose-500/10' : 'bg-white/5',
        highlight: 'bg-rose-500/20 text-rose-300'
      },
      pink: {
        border: isSelected ? 'border-2 border-luxury-pinkBright' : 'border-2 border-transparent hover:border-luxury-pinkBright/50',
        bg: isSelected ? 'bg-luxury-pinkBright/10' : 'bg-white/5',
        highlight: 'bg-luxury-pinkBright/20 text-pink-300'
      },
      amber: {
        border: isSelected ? 'border-2 border-amber-500' : 'border-2 border-transparent hover:border-amber-500/50',
        bg: isSelected ? 'bg-amber-500/10' : 'bg-white/5',
        highlight: 'bg-amber-500/20 text-amber-300'
      }
    };
    return baseClasses[accentColor];
  };

  return (
    <StickyOnboardingFooter
      buttonText="Continuar"
      onClick={handleNext}
      buttonTestId="relationship-next-button"
    >
      <div className="max-w-3xl mx-auto" data-testid="relationship-step-root">
        <div className="text-center mb-4 md:mb-8">
          <h2 className="text-xl md:text-3xl font-bold text-white">
            {screenConfig.title}
          </h2>
          <p className="text-xs md:text-sm text-white/60 mt-1 md:mt-2">
            {screenConfig.subtitle}
          </p>
        </div>

        <div 
          role="radiogroup" 
          aria-label="Tipo de relacionamento"
          className="flex flex-col gap-2 md:grid md:grid-cols-3 md:gap-4"
        >
          {screenConfig.options.map((option: RelationshipOption) => {
            const isSelected = selectedType === option.id;
            const accentClasses = getAccentClasses(option.accentColor, isSelected);
            const testId = `relationship-option-${option.id.replace('_', '-')}`;
            
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => handleSelectType(option.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectType(option.id);
                  }
                }}
                data-testid={testId}
                className={cn(
                  "relative rounded-xl transition-all duration-300",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-luxury-pinkBright",
                  accentClasses.border,
                  accentClasses.bg,
                  // Mobile: horizontal layout, compact
                  "flex flex-row items-center p-3 gap-3",
                  // Desktop: vertical layout, full
                  "md:flex-col md:items-center md:text-center md:p-6 md:gap-0"
                )}
              >
                {/* Selection indicator - desktop only */}
                {isSelected && (
                  <div className="hidden md:flex absolute top-3 right-3 w-5 h-5 rounded-full bg-white/20 items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-white" />
                  </div>
                )}

                {/* Emoji - smaller on mobile */}
                <div className="text-3xl md:text-5xl md:mb-4 flex-shrink-0">
                  {option.emoji}
                </div>

                {/* Content container */}
                <div className="flex-1 text-left md:text-center min-w-0">
                  {/* Title + mobile indicator */}
                  <div className="flex items-center gap-2 md:block">
                    <h3 className="text-base md:text-lg font-semibold text-white truncate">
                      {option.label}
                    </h3>
                    {/* Mobile selection indicator */}
                    {isSelected && (
                      <div className="md:hidden w-4 h-4 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-white" />
                      </div>
                    )}
                  </div>

                  {/* Subtitle - always visible */}
                  <p className="text-xs md:text-sm text-white/60 md:mb-4 line-clamp-1 md:line-clamp-none">
                    {option.highlight}
                  </p>

                  {/* Description - hidden on mobile unless selected */}
                  <p className={cn(
                    "text-xs text-white/50 leading-relaxed md:mb-4",
                    "hidden md:block",
                    isSelected && "block mt-1"
                  )}>
                    {option.description}
                  </p>

                  {/* Chips - hidden on mobile unless selected */}
                  <div className={cn(
                    "flex flex-wrap gap-1.5 md:gap-2 md:justify-center mt-2",
                    "hidden md:flex",
                    isSelected && "flex"
                  )}>
                    {option.chips.map((chip, index) => (
                      <span
                        key={index}
                        className={cn(
                          "text-[10px] md:text-xs px-2 py-0.5 md:py-1 rounded-full",
                          accentClasses.highlight
                        )}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {showError && !selectedType && (
          <div 
            role="alert" 
            aria-live="polite"
            className="mt-4 text-red-400 text-sm text-center"
            data-testid="relationship-error-message"
          >
            Por favor, selecione o tipo de conexão que você busca.
          </div>
        )}
      </div>
    </StickyOnboardingFooter>
  );
};

export default RelationshipTypeStep;
